import { useState, useCallback, useMemo } from "react";
import { View, Text, Pressable, ScrollView, Modal } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { X, SlidersHorizontal } from "lucide-react-native";
import { EMBER } from "../../../onboarding/theme";
import { GLASS_CARD_STYLE } from "@louise/moon-ui/theme/tokens";
import {
  BrownButton,
  IconButton,
  MoonFilterChip,
  MoonCheckbox,
  TextActionButton,
} from "@louise/moon-ui";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import type {
  GrantsFilters,
  GrantTreatment,
  ApplicationStatus,
} from "./grants.types";
import { GRANTS } from "./grants.data";
import { filterGrants } from "./grants.utils";

const TREATMENT_OPTIONS: { label: string; value: GrantTreatment }[] = [
  { label: "IVF", value: "IVF" },
  { label: "IUI", value: "IUI" },
  { label: "Egg Freezing", value: "Egg Freezing" },
  { label: "Donor Egg", value: "Donor Egg" },
  { label: "Embryo Adoption", value: "Embryo Adoption" },
  { label: "Surrogacy", value: "Surrogacy" },
  { label: "Adoption", value: "Adoption" },
];

const STATUS_OPTIONS: { label: string; value: ApplicationStatus }[] = [
  { label: "Open Now", value: "open" },
  { label: "Upcoming", value: "upcoming" },
  { label: "Closed", value: "closed" },
];

function ToggleRow({
  label,
  active,
  onPress,
}: {
  label: string;
  active: boolean;
  onPress: () => void;
}) {
  return (
    <MoonCheckbox
      reverse
      label={label}
      value={active}
      onChange={onPress}
      accentColor={EMBER.colors.warm}
      style={{
        paddingVertical: 14,
        borderBottomWidth: 1,
        borderBottomColor: "rgba(0,0,0,0.04)",
      }}
    />
  );
}

type Props = {
  filters: GrantsFilters;
  onFiltersChange: (filters: GrantsFilters) => void;
};

export default function GrantsFilterModal({ filters, onFiltersChange }: Props) {
  const capturePress = usePressCapture("GrantsFilterModal");
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<GrantsFilters>(filters);

  const previewCount = useMemo(
    () => filterGrants(GRANTS, draft).length,
    [draft],
  );

  const handleOpen = useCallback(() => {
    // Icon-only trigger with no visible copy, so the descriptor is the control.
    capturePress(pressLabel("open_grants_filters"));
    setDraft({ ...filters });
    setOpen(true);
  }, [capturePress, filters]);

  const handleApply = useCallback(() => {
    onFiltersChange({ ...draft, search: filters.search });
    setOpen(false);
  }, [draft, filters.search, onFiltersChange]);

  const handleClear = useCallback(() => {
    setDraft({ search: filters.search });
  }, [filters.search]);

  const toggleTreatment = useCallback((treatment: GrantTreatment) => {
    setDraft((prev) => {
      const current = prev.treatments ?? [];
      const has = current.includes(treatment);
      return {
        ...prev,
        treatments: has
          ? current.filter((t) => t !== treatment)
          : [...current, treatment],
      };
    });
  }, []);

  const toggleStatus = useCallback((status: ApplicationStatus) => {
    setDraft((prev) => {
      const current = prev.applicationStatus ?? [];
      const has = current.includes(status);
      return {
        ...prev,
        applicationStatus: has
          ? current.filter((s) => s !== status)
          : [...current, status],
      };
    });
  }, []);

  const mainActiveCount = useMemo(() => {
    let count = 0;
    if (filters.treatments?.length) count += filters.treatments.length;
    if (filters.applicationStatus?.length) {
      count += filters.applicationStatus.length;
    }
    if (filters.matchesUser) count++;
    return count;
  }, [filters]);

  return (
    <>
      <Pressable
        onPress={handleOpen}
        style={{
          width: 44,
          height: 44,
          borderRadius: 22,
          ...(GLASS_CARD_STYLE as object),
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <SlidersHorizontal
          size={18}
          color={EMBER.colors.warm}
          strokeWidth={1.5}
        />
        {mainActiveCount > 0 && (
          <View
            style={{
              position: "absolute",
              top: -2,
              right: -2,
              width: 18,
              height: 18,
              borderRadius: 9,
              backgroundColor: EMBER.colors.warm,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Text
              className="font-dm-sans"
              style={{ fontSize: 10, fontWeight: "700", color: "#fff" }}
            >
              {mainActiveCount}
            </Text>
          </View>
        )}
      </Pressable>

      <Modal
        visible={open}
        transparent
        animationType="slide"
        onRequestClose={() => setOpen(false)}
      >
        <View style={{ flex: 1, backgroundColor: "rgba(0,0,0,0.4)" }}>
          <View style={{ flex: 0.08 }}>
            <Pressable
              style={{ flex: 1 }}
              onPress={() => {
                // Dismiss tap target has no visible copy of its own.
                capturePress(pressLabel("dismiss_grants_filters"));
                setOpen(false);
              }}
            />
          </View>
          <View
            style={{
              flex: 1,
              backgroundColor: EMBER.colors.surface,
              borderTopLeftRadius: 24,
              borderTopRightRadius: 24,
              overflow: "hidden",
            }}
          >
            <SafeAreaView style={{ flex: 1 }} edges={["bottom"]}>
              {/* Header */}
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  paddingHorizontal: 20,
                  paddingTop: 20,
                  paddingBottom: 16,
                  borderBottomWidth: 1,
                  borderBottomColor: "rgba(0,0,0,0.06)",
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 22,
                    fontWeight: "700",
                    color: EMBER.colors.text,
                  }}
                >
                  Filters
                </Text>
                <IconButton
                  onPress={() => setOpen(false)}
                  hitSlop={8}
                  size={32}
                  surface="soft"
                  backgroundColor="rgba(0,0,0,0.05)"
                  pressedBackgroundColor="rgba(0,0,0,0.05)"
                  pressedOpacity={1}
                  accessibilityLabel="Close"
                >
                  <X size={16} color={EMBER.colors.textLight} />
                </IconButton>
              </View>

              {/* Content */}
              <ScrollView
                contentContainerStyle={{
                  paddingHorizontal: 20,
                  paddingBottom: 20,
                }}
                showsVerticalScrollIndicator={false}
              >
                {/* Treatment Type */}
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 13,
                    fontWeight: "700",
                    color: EMBER.colors.textMid,
                    letterSpacing: 0.4,
                    textTransform: "uppercase",
                    marginTop: 20,
                    marginBottom: 12,
                  }}
                >
                  Treatment Type
                </Text>
                <View
                  style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}
                >
                  {TREATMENT_OPTIONS.map((opt) => (
                    <MoonFilterChip
                      key={opt.value}
                      label={opt.label}
                      selected={draft.treatments?.includes(opt.value) ?? false}
                      onPress={() => toggleTreatment(opt.value)}
                    />
                  ))}
                </View>

                {/* Application Status */}
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 13,
                    fontWeight: "700",
                    color: EMBER.colors.textMid,
                    letterSpacing: 0.4,
                    textTransform: "uppercase",
                    marginTop: 28,
                    marginBottom: 12,
                  }}
                >
                  Application Status
                </Text>
                <View style={{ flexDirection: "row", gap: 8 }}>
                  {STATUS_OPTIONS.map((opt) => (
                    <MoonFilterChip
                      key={opt.value}
                      label={opt.label}
                      selected={
                        draft.applicationStatus?.includes(opt.value) ?? false
                      }
                      onPress={() => toggleStatus(opt.value)}
                    />
                  ))}
                </View>

                {/* Other */}
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 13,
                    fontWeight: "700",
                    color: EMBER.colors.textMid,
                    letterSpacing: 0.4,
                    textTransform: "uppercase",
                    marginTop: 28,
                    marginBottom: 4,
                  }}
                >
                  Other
                </Text>
                <ToggleRow
                  label="Matches My Profile"
                  active={draft.matchesUser ?? false}
                  onPress={() =>
                    setDraft((prev) => ({
                      ...prev,
                      matchesUser: !prev.matchesUser,
                    }))
                  }
                />
              </ScrollView>

              {/* Footer */}
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 12,
                  paddingHorizontal: 20,
                  paddingVertical: 16,
                  borderTopWidth: 1,
                  borderTopColor: "rgba(0,0,0,0.06)",
                }}
              >
                <TextActionButton
                  label="Clear all"
                  onPress={handleClear}
                  color={EMBER.colors.textMid}
                />
                <BrownButton
                  label={`Show ${previewCount} Grant${previewCount !== 1 ? "s" : ""}`}
                  onPress={handleApply}
                  shape="pill"
                  fullWidth={false}
                  style={{ flex: 1 }}
                  textStyle={{ fontWeight: "700" }}
                />
              </View>
            </SafeAreaView>
          </View>
        </View>
      </Modal>
    </>
  );
}
